import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ApiCustomerSearchResponse {
  items: CustomerSearchItem[];
  totalCount: number;
  pageNumber: number;
  maxItems: number;
}

export interface CustomerSearchItem {
  createdOn: string;
  lastUpdatedOn: string;
  eventDate: string;
  expiryDate: string;
  surveyRequestId: string;
  vin: string;
  externalRecordId: string;
  segmentId: string;
  businessCode: string;
  businessName: string;
  country: string;
  alertRaised: boolean;
  campaignName: string;
  surveyId: string;
  recordDiscardReason: string;
  customerName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  emailDiscardReason: string;
  emailDeliveryDate: string;
  mobilePhone: string;
  mobileDiscardReason: string;
  mobileDeliveryDate: string;
  fileName: string;
  responseChannel: string;
  responseDate: string;
  shortCode: string;
  surveyLink: string;
}

export interface PredefinedValuesResponse {
  emailDiscardReason: string[];
  mobileDiscardReason: string[];
  recordDiscardReason: string[];
  responseChannel: string[];
}

export interface CustomerDetailsResponse {
  customerData: CustomerSearchItem;
  emailNotifications: CustomerEmailNotification[];
  mobileNotifications: CustomerMobileNotification[];
  response: CustomerSurveyResponse | null;
}

interface CustomerNotificationBase {
  createdOn: string;
  lastModifiedOn: string;
  source: string;
  receivers: string;
  weekDays: string;
  dispatchUtcFrom: string;
  dispatchUtcTo: string;
  status: string;
  errorMessage: string | null;
  notificationVendorId: string;
  provider: string;
  externalStatusCode: string;
  deliveryDate: string | null;
  webHookErrorMessage: string | null;
}

export interface CustomerEmailNotification extends CustomerNotificationBase {
  subject: string;
  senderAddress: string;
}

export interface CustomerMobileNotification extends CustomerNotificationBase {
  alphaName: string;
  textMessage: string;
}

export interface CustomerSurveyAnswer {
  questionType: number;
  questionCode: string;
  textValue: string | null;
  numericValue: number | null;
  choiceValue: string | null;
  fieldName: string | null;
}

export interface CustomerCallDetails {
  id: string;
  calledToNumber: string;
  languagePreference: string;
  callStart: string;
  callEnd: string;
  importedSurvey: string;
  partialSurvey: boolean;
  externalId: string;
}

export interface CustomerSurveyResponse {
  answers: CustomerSurveyAnswer[];
  recoveryAlertEvaluated: boolean;
  recoveryAlertEvaluatedCount: number;
  recoverySurveyResultPushed: boolean;
  recoverySurveyResultPushedCount: number;
  responseAcquired: boolean;
  responseAcquiredDate: string | null;
  exportedAntiFraud: boolean;
  channel: string;
  surveyStarted: string | null;
  responseSubmitted: string | null;
  fraudExitReason: string | null;
  isWhatsapp: boolean;
  fraudSuspiciusResult: string | null;
  callDetails: CustomerCallDetails | null;
}

export type CustomerSearchCriteria = Record<string, string>;

export type ApiCustomerSearchErrorCode = "CS400" | "CS500";

@Injectable({ providedIn: 'root' })
export class ApiCustomerSearchService {
  private readonly apiBaseUrl = environment.apiBaseUrl;
  private readonly apiSearchPath = '/customer/search';
  private readonly apiDetailsPath = '/customer/details';
  private readonly apiPredefinedPath = '/customer/search/predefined-values';

  constructor(
    private http: HttpClient) {}

  searchCustomers(organizationId: string, criteria: CustomerSearchCriteria, pageNumber: number, maxItems: number, sortDirection: "asc" | "desc"): Observable<ApiCustomerSearchResponse> {
    const request: Record<string, unknown> = {
      organizationId,
      pageNumber,
      maxItems,
      sortDirection,
      ...criteria
    };

    // alertRaised is a boolean on the backend — the form only ever produces the string "true" /
    // "false" (or leaves it unset), so convert it here rather than sending it as text.
    if (typeof request['alertRaised'] === 'string') {
      request['alertRaised'] = request['alertRaised'] === 'true';
    }

    return this.http.post<ApiCustomerSearchResponse>(`${this.apiBaseUrl}${this.apiSearchPath}`, request)
  }

  predefinedValues(): Observable<PredefinedValuesResponse> {
    return this.http.get<PredefinedValuesResponse>(`${this.apiBaseUrl}${this.apiPredefinedPath}`)
  }

  customerDetail(surveyRequestId: string): Observable<CustomerDetailsResponse> {
    const params = new HttpParams()
      .set('surveyRequestId', surveyRequestId);

    return this.http.get<CustomerDetailsResponse>(`${this.apiBaseUrl}${this.apiDetailsPath}`, {params})
  }
}